import { useState } from "react";
import { C } from "../constants";
import { Sec, STitle, Btn } from "../components/Shared";

function VideoPlaceholder({ title, duration, desc, color }) {
  return (
    <div style={{
      background: C.white, borderRadius: 14, overflow: "hidden",
      border: `1px solid ${C.border}`, transition: "transform 0.2s"
    }}
      onMouseEnter={e => e.currentTarget.style.transform = "translateY(-3px)"}
      onMouseLeave={e => e.currentTarget.style.transform = ""}
    >
      <div style={{
        aspectRatio: "16/9", background: color || C.blueLight,
        display: "flex", alignItems: "center", justifyContent: "center",
        cursor: "pointer", position: "relative"
      }}>
        <div style={{
          width: 56, height: 56, borderRadius: "50%", background: "rgba(255,255,255,0.9)",
          display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: "0 4px 16px rgba(0,0,0,0.1)"
        }}>
          <span style={{ fontSize: 22, marginLeft: 3, color: C.blue }}>▶</span>
        </div>
        <div style={{
          position: "absolute", top: 10, right: 10, background: "rgba(0,0,0,0.6)",
          borderRadius: 6, padding: "3px 8px", fontFamily: "'Nunito', sans-serif",
          fontSize: 11, fontWeight: 600, color: "#fff"
        }}>{duration}</div>
      </div>
      <div style={{ padding: "16px 18px" }}>
        <h4 style={{ fontFamily: "'Nunito', sans-serif", fontWeight: 700, fontSize: 15, color: C.text, margin: "0 0 4px" }}>{title}</h4>
        <p style={{ fontFamily: "'Nunito', sans-serif", fontSize: 13, color: C.textMid, lineHeight: 1.5, margin: 0 }}>{desc}</p>
      </div>
    </div>
  );
}

function MsgDemo() {
  const [input, setInput] = useState("");
  const [result, setResult] = useState(null);

  const check = () => {
    if (!input.trim()) return;
    const bads = ["stupid", "ugly", "hate", "dumb", "idiot", "shut up", "loser", "fat", "suck", "die", "kill"];
    const lower = input.toLowerCase();
    const found = bads.some(w => lower.includes(w));
    if (found) {
      setResult({ blocked: true, original: input, rewrite: "I'm feeling frustrated right now. Can we talk about it?" });
    } else {
      setResult({ blocked: false, original: input });
    }
  };

  return (
    <div style={{ maxWidth: 480, margin: "0 auto", background: C.white, borderRadius: 18, padding: 28, border: `1px solid ${C.border}` }}>
      <div style={{ display: "flex", gap: 8 }}>
        <input value={input} onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === "Enter" && check()}
          placeholder="Type a message to test..."
          style={{
            flex: 1, padding: "11px 14px", borderRadius: 10, border: `1px solid ${C.border}`,
            fontFamily: "'Nunito', sans-serif", fontSize: 14.5, outline: "none"
          }} />
        <Btn primary onClick={check}>Send</Btn>
      </div>
      {result && (
        <div style={{ marginTop: 18 }}>
          {result.blocked ? (
            <>
              <div style={{ background: "#FEF2F2", borderRadius: 10, padding: 14, marginBottom: 10, border: "1px solid #FECACA" }}>
                <div style={{ fontFamily: "'Nunito', sans-serif", fontWeight: 700, fontSize: 13.5, color: C.danger, marginBottom: 3 }}>⚠️ Message blocked</div>
                <p style={{ fontFamily: "'Nunito', sans-serif", fontSize: 13.5, color: C.textLight, margin: 0, textDecoration: "line-through" }}>{result.original}</p>
              </div>
              <div style={{ background: C.accentLight, borderRadius: 10, padding: 14, border: "1px solid #A7F3D0" }}>
                <div style={{ fontFamily: "'Nunito', sans-serif", fontWeight: 700, fontSize: 13.5, color: C.accent, marginBottom: 3 }}>⚡ Suggested rewrite</div>
                <p style={{ fontFamily: "'Nunito', sans-serif", fontSize: 13.5, color: C.text, margin: "0 0 10px" }}>{result.rewrite}</p>
                <Btn primary style={{ padding: "7px 18px", fontSize: 13 }}>Send this instead</Btn>
              </div>
            </>
          ) : (
            <div style={{ background: C.accentLight, borderRadius: 10, padding: 14, border: "1px solid #A7F3D0" }}>
              <span style={{ fontFamily: "'Nunito', sans-serif", fontWeight: 700, fontSize: 13.5, color: C.accent }}>✅ Message approved — safe to send!</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Demo() {
  return (
    <div>
      {/* Main demo video */}
      <Sec>
        <STitle tag="Demo" title="See SafeZapp in action" sub="Watch our overview video, then explore the step-by-step tutorials below" />
        <div style={{
          maxWidth: 780, margin: "0 auto", borderRadius: 18, overflow: "hidden",
          border: `1px solid ${C.border}`, boxShadow: "0 12px 40px rgba(59,154,232,0.15)"
        }}>
          <div style={{ position: "relative", paddingTop: "56.25%" }}>
            <iframe
              title="SafeZapp Full Product Demo"
              src="https://customer-y9wfhjj6qzjf9b3b.cloudflarestream.com/6d7fdc6d0f6e2d6b88eb27b8b88f4b9b/iframe?poster=https%3A%2F%2Fcustomer-y9wfhjj6qzjf9b3b.cloudflarestream.com%2F6d7fdc6d0f6e2d6b88eb27b8b88f4b9b%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600"
              loading="lazy"
              style={{ border: "none", position: "absolute", top: 0, left: 0, height: "100%", width: "100%" }}
              allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
              allowFullScreen
            ></iframe>
          </div>
        </div>
      </Sec>

      {/* Tutorial videos */}
      <Sec bg={C.bg}>
        <STitle tag="Tutorials" title="Step-by-step video guides" sub="Short walkthroughs showing you exactly how each feature works" />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 22 }}>
          <VideoPlaceholder title="Setting Up as an Approver" duration="2:30" desc="How to create your approver account, generate pairing codes, and link with your user." color={C.blueLight} />
          <VideoPlaceholder title="Getting Started as a User" duration="2:00" desc="Downloading the app, entering a pairing code, and sending your first message." color={C.accentLight} />
          <VideoPlaceholder title="The Pairing Code Process" duration="1:30" desc="A walkthrough of how the 6-digit pairing code links approvers and users securely." color={C.yellowLight} />
          <VideoPlaceholder title="Using the Approver Dashboard" duration="3:00" desc="Managing contacts, approving requests, and adjusting settings for your users." color={C.blueLight} />
          <VideoPlaceholder title="AI Message Filtering in Action" duration="2:00" desc="See how SafeZapp detects harmful content and suggests kinder rewrites in real-time." color={C.accentLight} />
          <VideoPlaceholder title="Managing Multiple Users" duration="1:45" desc="How to link multiple users to a single approver account with individual settings." color={C.yellowLight} />
        </div>
      </Sec>

      {/* Interactive demo */}
      <Sec>
        <STitle title="Try the AI filter yourself" sub="Type a message below to see how SafeZapp's content screening works" />
        <MsgDemo />
      </Sec>
    </div>
  );
}
